import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Collapse, Box, Typography, IconButton, Button, Grid, Modal, useMediaQuery, useTheme, Card, CardContent, Divider } from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import StateContext from "../../Contexts/StateContext";
import DispatchContext from "../../Contexts/DispatchContext";
import AddScientist from './AddScientist';
import AddPublication from './AddPublication';
import EditScientist from './EditScientist';
import EditPublication from './EditPublication';

function Academy() {
  const { t } = useTranslation();
  const { config, environment } = require('../../index');
  const GlobalState = useContext(StateContext);
  const dispatchGlobal = useContext(DispatchContext);
  const navigate = useNavigate();
  const currentUser = GlobalState.userId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [centuries, setCenturies] = useState({});
  const [publications, setPublications] = useState({});
  const [openCenturies, setOpenCenturies] = useState({});
  const [openFields, setOpenFields] = useState({});
  const [openPublications, setOpenPublications] = useState({});
  const [totalScientists, setTotalScientists] = useState(0);
  const [currentFullReference, setCurrentFullReference] = useState(null);

  // Modals
  const [isAddScientistModalOpen, setAddScientistModalOpen] = useState(false);
  const [isAddPublicationModalOpen, setAddPublicationModalOpen] = useState(false);
  const [isEditScientistOpen, setIsEditScientistOpen] = useState(false);
  const [isEditPublicationOpen, setIsEditPublicationOpen] = useState(false);
  const [currentScientist, setCurrentScientist] = useState(null);
  const [currentPublication, setCurrentPublication] = useState(null);

  // Fetch profile
  useEffect(() => {
    if (!GlobalState.jwtAccessToken || !GlobalState.userId || GlobalState.isLoaded) return;
    const fetchProfile = async () => {
      try {
        const profileEndpoint = config[process.env.NODE_ENV].getProfileEndpoint(GlobalState.userId);
        const res = await axios.get(profileEndpoint, {
          headers: { Authorization: `Bearer ${GlobalState.jwtAccessToken}` },
        });
        dispatchGlobal({ type: "catchUserProfileInfo", profile: res.data });
      } catch (err) {
        console.error("Failed to fetch profile:", err);
      }
    };
    fetchProfile();
  }, [GlobalState.jwtAccessToken, GlobalState.userId, GlobalState.isLoaded, dispatchGlobal]);

  // Fetch scientists
  useEffect(() => {
    const fetchScientists = async () => {
      try {
        const res = await axios.get(config[environment].scientistsEndpoint);
        const grouped = groupByCentury(res.data);
        setCenturies(grouped);
        setTotalScientists(res.data.length);
      } catch (err) {
        console.error("Failed to fetch scientists:", err);
      }
    };
    fetchScientists();
  }, [config, environment]);

  const groupByCentury = (data) => {
    const grouped = {};
    data.forEach(item => {
      if (!grouped[item.centuryid]) grouped[item.centuryid] = [];
      grouped[item.centuryid].push(item);
    });
    return grouped;
  };

  // Toggle helpers
  const toggleOpenCentury = (id) => setOpenCenturies(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleOpenField = (id) => setOpenFields(prev => ({ ...prev, [id]: !prev[id] }));

  const toggleOpenPublication = async (scientistId) => {
    setOpenPublications(prev => ({ ...prev, [scientistId]: !prev[scientistId] }));

    if (!publications[scientistId]) {
      try {
        const res = await axios.get(config[environment].getPublicationsEndpoint(scientistId));
        setPublications(prev => ({ ...prev, [scientistId]: res.data }));
      } catch (err) {
        console.error("Publikáció lekérés hiba:", err);
      }
    }
  };

  const handleEditScientist = (scientist) => {
    setCurrentScientist(scientist);
    setIsEditScientistOpen(true);
  };

  const handleEditPublication = (publication) => {
    setCurrentPublication(publication);
    setIsEditPublicationOpen(true);
  };

  const handleDeleteScientist = async (scientistId) => {
    try {
      await axios.delete(config[environment].deleteScientistEndpoint(scientistId));
      const updated = { ...centuries };
      Object.keys(updated).forEach(cId => {
        updated[cId] = updated[cId].filter(s => s.scientistid !== scientistId);
      });
      setCenturies(updated);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeletePublication = async (publicationId, scientistId) => {
    try {
      await axios.delete(config[environment].deletePublicationEndpoint(publicationId));
      setPublications(prev => ({
        ...prev,
        [scientistId]: prev[scientistId].filter(p => p.id !== publicationId)
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const AddContentButtonDisplay = () => {
    return GlobalState.userIsLogged ? (
        <>
          <Button variant="contained" color="primary" sx={{ m: 1 }} onClick={() => setAddScientistModalOpen(true)}>{t('Academy.AddNewScientist')}</Button>
          <Button variant="contained" color="primary" sx={{ m: 1 }} onClick={() => setAddPublicationModalOpen(true)}>{t('Academy.AddNewPublication')}</Button>
        </>
    ) : (
        <Button variant="outlined" onClick={() => navigate('/login')}>{t('Academy.LoginToAdd')}</Button>
    );
  };

  const RenderCentury = ({ centuryId, centuryData }) => {
    const groupedFields = Object.values(centuryData.reduce((acc, scientist) => {
      const fieldId = scientist.fieldofscienceid;
      if (!acc[fieldId]) acc[fieldId] = { ...scientist, scientists: [] };
      acc[fieldId].scientists.push(scientist);
      return acc;
    }, {}));

    if (isMobile) {
      return (
        <Box key={centuryId} sx={{ mb: 2, p: 1, border: '1px solid #ddd', borderRadius: 2 }}>
          <Box 
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}
            onClick={() => toggleOpenCentury(centuryId)}
          >
            <IconButton size="small">
              {openCenturies[centuryId] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
            <Typography variant="h6" sx={{ color: "#105b79", fontWeight: 'bold' }}>
              {centuryData[0]?.centuryname || t('Academy.UnknownCentury')}
            </Typography>
          </Box>
          <Collapse in={openCenturies[centuryId]} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1, pl: 1 }}>
              {groupedFields.map(field => (
                <Box key={field.fieldofscienceid} sx={{ mb: 1.5 }}>
                  <Box 
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid #eee', pb: 0.5 }}
                    onClick={() => toggleOpenField(field.fieldofscienceid)}
                  >
                    {openFields[field.fieldofscienceid] ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                    <Typography sx={{ fontWeight: "bold", color: "#032b3b", fontSize: '0.9rem' }}>
                      {field.fieldofsciencename}
                    </Typography>
                  </Box>
                  <Collapse in={openFields[field.fieldofscienceid]} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 1, pl: 1 }}>
                      {field.scientists.map(scientist => (
                        <Card key={scientist.scientistid} sx={{ mb: 2, boxShadow: 2 }}>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#105b79' }}>
                                {`${scientist.scientistlastname} ${scientist.scientistfirstname}`}
                              </Typography>
                              <Box>
                                {GlobalState.userIsLogged && currentUser === scientist.scientistcontributorid && (
                                  <>
                                    <IconButton size="small" onClick={() => handleEditScientist(scientist)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => handleDeleteScientist(scientist.scientistid)}><DeleteIcon fontSize="small" /></IconButton>
                                  </>
                                )}
                              </Box>
                            </Box>
                            
                            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1 }}>{scientist.scientistthesistitle}</Typography>
                            
                            <Grid container spacing={0.5} sx={{ fontSize: '0.8rem' }}>
                              <Grid item xs={6}><Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{t('Academy.Birth')}:</Typography> {scientist.scientistbirthplace}, {scientist.scientistbirthdate}</Grid>
                              <Grid item xs={6}><Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{t('Academy.Death')}:</Typography> {scientist.scientistdeathplace}, {scientist.scientistdeathdate}</Grid>
                              <Grid item xs={12}><Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{t('Academy.Tie')}:</Typography> {scientist.scientistszeklertie}</Grid>
                            </Grid>

                            <Button 
                              size="small" 
                              fullWidth 
                              variant="outlined" 
                              sx={{ mt: 1, textTransform: 'none' }}
                              onClick={() => toggleOpenPublication(scientist.scientistid)}
                              endIcon={openPublications[scientist.scientistid] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                            >
                              {t('Academy.Publications')}
                            </Button>

                            <Collapse in={openPublications[scientist.scientistid]} timeout="auto" unmountOnExit>
                              <Box sx={{ mt: 1, p: 1, bgcolor: '#fafafa', borderRadius: 1 }}>
                                {publications[scientist.scientistid]?.length > 0 ? (
                                  publications[scientist.scientistid].map(pub => (
                                    <Box key={pub.id} sx={{ mb: 1, pb: 1, borderBottom: '1px dashed #ccc', '&:last-child': { borderBottom: 'none', mb: 0, pb: 0 } }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{pub.title}</Typography>
                                          <Typography variant="caption" display="block">
                                            {pub.publication_date ? new Date(pub.publication_date).getFullYear() : ''} | {pub.publisher}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          {GlobalState.userIsLogged && currentUser === pub.contributor && (
                                              <>
                                                <IconButton size="small" onClick={() => handleEditPublication(pub)}><EditIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" onClick={() => handleDeletePublication(pub.id, scientist.scientistid)}><DeleteIcon fontSize="small" /></IconButton>
                                              </>
                                          )}
                                        </Box>
                                      </Box>
                                      <Button size="small" sx={{ p: 0, minWidth: 0, textTransform: 'none' }} onClick={() => setCurrentFullReference(pub.full_reference)}>
                                        {t('Academy.ViewFullReference')}
                                      </Button>
                                    </Box>
                                  ))
                                ) : (
                                  <Typography variant="caption" sx={{ fontStyle: 'italic' }}>{t('Academy.NoPublications')}</Typography>
                                )}
                              </Box>
                            </Collapse>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      );
    }

    return (
        <React.Fragment key={centuryId}>
          <TableRow>
            <TableCell>
              <IconButton size="small" onClick={() => toggleOpenCentury(centuryId)}>
                {openCenturies[centuryId] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </IconButton>
            </TableCell>
            <TableCell sx={{ fontSize: "1.8rem" }}>{centuryData[0]?.centuryname || t('Academy.NoCenturyData')}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={6} style={{ paddingBottom: 0, paddingTop: 0 }}>
              <Collapse in={openCenturies[centuryId]} timeout="auto" unmountOnExit>
                <Box margin={1.5}>
                  {groupedFields.map(field => (
                      <React.Fragment key={field.fieldofscienceid}>
                        <TableRow onClick={() => toggleOpenField(field.fieldofscienceid)}>
                          <TableCell sx={{ display: "flex", alignItems: "center", fontWeight: "bold", color: "#032b3b" }}>
                            {field.scientists.length > 0 && (openFields[field.fieldofscienceid] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />)}
                            {field.fieldofsciencename}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6} style={{ paddingBottom: 0, paddingTop: 0 }}>
                            <Collapse in={openFields[field.fieldofscienceid]} timeout="auto" unmountOnExit>
                              <Box margin={1.5}>
                                <Typography sx={{ fontSize: "1rem" }} gutterBottom>{t('Academy.Scientists')}</Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ fontStyle: "italic", color: "#2492be" }}>
                                      <TableCell />
                                      <TableCell>{t('Academy.Name')}</TableCell>
                                      <TableCell>{t('Academy.ThesisTitle')}</TableCell>
                                      <TableCell>{t('Academy.BirthPlace')}</TableCell>
                                      <TableCell>{t('Academy.BirthDate')}</TableCell>
                                      <TableCell>{t('Academy.DeathPlace')}</TableCell>
                                      <TableCell>{t('Academy.DeathDate')}</TableCell>
                                      <TableCell>{t('Academy.SzeklerTie')}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {field.scientists.map(scientist => (
                                        <React.Fragment key={scientist.scientistid}>
                                          {/* Scientist row */}
                                          <TableRow hover>
                                            <TableCell>
                                              <IconButton onClick={() => toggleOpenPublication(scientist.scientistid)}>
                                                {openPublications[scientist.scientistid] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                              </IconButton>
                                            </TableCell>
                                            <TableCell>{`${scientist.scientistlastname} ${scientist.scientistfirstname}`}</TableCell>
                                            <TableCell>{scientist.scientistthesistitle}</TableCell>
                                            <TableCell>{scientist.scientistbirthplace}</TableCell>
                                            <TableCell>{scientist.scientistbirthdate}</TableCell>
                                            <TableCell>{scientist.scientistdeathplace}</TableCell>
                                            <TableCell>{scientist.scientistdeathdate}</TableCell>
                                            <TableCell>{scientist.scientistszeklertie}</TableCell>

                                            {GlobalState.userIsLogged && currentUser === scientist.scientistcontributorid && (
                                                <>
                                                  <TableCell>
                                                    <IconButton onClick={() => handleEditScientist(scientist)}><EditIcon /></IconButton>
                                                  </TableCell>
                                                  <TableCell>
                                                    <IconButton onClick={() => handleDeleteScientist(scientist.scientistid)}><DeleteIcon /></IconButton>
                                                  </TableCell>
                                                </>
                                            )}
                                          </TableRow>

                                          {/* Publications Collapse */}
                                          <TableRow>
                                            <TableCell colSpan={10} style={{ paddingBottom: 0, paddingTop: 0 }}>
                                              <Collapse in={openPublications[scientist.scientistid]} timeout="auto" unmountOnExit>
                                                <Box margin={1.5}>
                                                  <Typography variant="subtitle2" gutterBottom>{t('Academy.Publications')}</Typography>
                                                  <Table size="small">
                                                    <TableHead>
                                                      <TableRow>
                                                        <TableCell>ID</TableCell>
                                                        <TableCell>{t('Academy.Title')}</TableCell>
                                                        <TableCell>{t('Academy.Year')}</TableCell>
                                                        <TableCell>{t('Academy.Source')}</TableCell>
                                                        <TableCell>{t('Academy.FullReference')}</TableCell>
                                                        <TableCell></TableCell>
                                                        <TableCell></TableCell>
                                                      </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                      {publications[scientist.scientistid]?.map(pub => (
                                                          <TableRow key={pub.id}>
                                                            <TableCell>{pub.id}</TableCell>
                                                            <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 250 }}>
                                                              {pub.title}
                                                            </TableCell>
                                                            <TableCell>{pub.publication_date ? new Date(pub.publication_date).getFullYear() : ''}</TableCell>
                                                            <TableCell>{pub.publisher}</TableCell>
                                                            <TableCell>
                                                              <Button size="small" onClick={() => setCurrentFullReference(pub.full_reference)}>
                                                                {t('Academy.View')}
                                                              </Button>
                                                            </TableCell>
                                                            {GlobalState.userIsLogged && currentUser === pub.contributor && (
                                                                <>
                                                                    <TableCell>
                                                                        <IconButton onClick={() => handleEditPublication(pub)}><EditIcon /></IconButton>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <IconButton onClick={() => handleDeletePublication(pub.id, scientist.scientistid)}><DeleteIcon /></IconButton>
                                                                    </TableCell>
                                                                </>
                                                            )}
                                                          </TableRow>
                                                      )) || (
                                                          <TableRow>
                                                            <TableCell colSpan={5} sx={{ fontStyle: "italic" }}>{t('Academy.NoPublications')}</TableCell>
                                                          </TableRow>
                                                      )}
                                                    </TableBody>
                                                  </Table>
                                                </Box>
                                              </Collapse>
                                            </TableCell>
                                          </TableRow>
                                        </React.Fragment>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                  ))}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        </React.Fragment>
    );
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 3, mt: '50px' }}>
      <Typography variant={isMobile ? "h5" : "h4"} sx={{ textAlign: "center", color: "#105b79", fontWeight: "bold", margin: isMobile ? "1rem 0" : "2rem 0" }}>
        {t('Academy.TitleMain')}
      </Typography>
      <Typography variant={isMobile ? "body1" : "h6"} sx={{ textAlign: "center", color: "#105b79", fontStyle: "italic", mb: 2 }}>
        {t('Academy.RegisteredScientists')}: {totalScientists}
      </Typography>
      
      <Grid container spacing={1} justifyContent="center" sx={{ mb: 3 }}>
        {AddContentButtonDisplay()}
      </Grid>

      <Grid container spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 3 }}>
        <Grid item><WarningIcon color="error" /></Grid>
        <Grid item xs={10} sm={8}>
          <Typography variant="body2" sx={{ fontStyle: "italic", color: "#105b79" }}>
            {t('Academy.EditWarning')}
          </Typography>
        </Grid>
      </Grid>

      {isMobile ? (
        <Box>
          {Object.keys(centuries).map(centuryId => (
            <RenderCentury key={centuryId} centuryId={centuryId} centuryData={centuries[centuryId]} />
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell sx={{ fontWeight: "bold", color: "#105b79" }}>{t('Academy.CenturyLabel')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(centuries).map(centuryId => (
                  <RenderCentury key={centuryId} centuryId={centuryId} centuryData={centuries[centuryId]} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Full reference modal */}
      <Modal
          open={!!currentFullReference}
          onClose={() => setCurrentFullReference(null)}
          aria-labelledby="full-reference-modal"
          aria-describedby="full-reference-text"
      >
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 600, bgcolor: 'background.paper', boxShadow: 24, p: 4, maxHeight: '80vh', overflowY: 'auto' }}>
          <Typography id="full-reference-text" sx={{ whiteSpace: 'pre-wrap' }}>
            {currentFullReference}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setCurrentFullReference(null)}>{t('Academy.Close')}</Button>
          </Box>
        </Box>
      </Modal>

      <AddScientist open={isAddScientistModalOpen} onClose={() => setAddScientistModalOpen(false)} />
      <AddPublication open={isAddPublicationModalOpen} onClose={() => setAddPublicationModalOpen(false)} />
      <EditScientist open={isEditScientistOpen} onClose={() => setIsEditScientistOpen(false)} scientist={currentScientist} />
      <EditPublication open={isEditPublicationOpen} onClose={() => setIsEditPublicationOpen(false)} publication={currentPublication} />
    </Box>
  );
}

export default Academy;
